import { Helmet } from 'react-helmet-async';

/**
 * SEO Component Guidelines:
 * 
 * TITLE FORMAT: "[Page Title] | CSW | Online Workspace | Productivity Tool | Study Stream"
 * - Pass only the page-specific title (e.g., "Study Timer"), site suffix is auto-appended
 * - Exception: Homepage and localized homepages can pass full custom titles
 * 
 * META DESCRIPTION: Unique per page, 150-160 characters max
 * - Include primary keyword naturally
 * - Describe page value proposition
 * 
 * H1 TAG: Each page must have exactly one H1 tag with primary keyword
 * H2 TAGS: Use for major sections
 */

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  /** Set to true for homepage/localized pages that have custom full titles */
  isHomepage?: boolean;
  /** Set to true to prevent indexing (e.g., redirect pages) */
  noIndex?: boolean;
}

const SITE_TITLE_SUFFIX = " | CSW | Online Workspace | Productivity Tool | Study Stream";

export const SEO = ({ 
  title, 
  description, 
  keywords,
  canonicalUrl,
  ogImage = "/og-image.png",
  isHomepage = false,
  noIndex = false
}: SEOProps) => {
  const siteUrl = "https://csw.live";
  
  // Handle canonicalUrl - support both full URLs and paths
  let fullUrl = siteUrl;
  if (canonicalUrl) {
    if (canonicalUrl.startsWith("https://") || canonicalUrl.startsWith("http://")) {
      // Already a full URL, use as-is
      fullUrl = canonicalUrl;
    } else {
      // It's a path, prepend siteUrl
      fullUrl = `${siteUrl}${canonicalUrl}`;
    }
  }
  
  // Strip the suffix if already present in the title to prevent duplication
  let cleanTitle = title;
  if (title.includes("| CSW |")) {
    // Extract just the page-specific part before the suffix
    cleanTitle = title.split("| CSW |")[0].trim();
  }
  
  // Auto-append site title suffix unless it's a homepage
  const fullTitle = isHomepage 
    ? title 
    : `${cleanTitle}${SITE_TITLE_SUFFIX}`;

  // Determine robots directive
  const robotsContent = noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />
      
      {/* Robots directives */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="bingbot" content={robotsContent} />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={`${siteUrl}${ogImage}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="CSW" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${siteUrl}${ogImage}`} />
    </Helmet>
  );
};
